import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Inbox, Send, Archive, Trash2, RefreshCw, Search, MailOpen, Mail } from "lucide-react";

export default function AdminInbox() {
  const [threads, setThreads] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [selected, setSelected] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [archived, setArchived] = useState(false);
  const [search, setSearch] = useState("");

  const loadThreads = () => {
    const p = new URLSearchParams();
    if (archived) p.set("archived", "true");
    if (search) p.set("search", search);
    return api.get(`/admin/inbox?${p.toString()}`).then((r) => {
      setThreads(r.data.threads || []);
      setTotalUnread(r.data.total_unread || 0);
    });
  };

  const openThread = async (t) => {
    setSelected(t);
    setReply("");
    const { data } = await api.get(`/admin/inbox/${t.id}`);
    setMsgs(data.messages || []);
    // Refresh unread badge
    loadThreads();
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setSending(true);
    try {
      await api.post(`/admin/inbox/${selected.id}/reply`, {
        body_html: reply.replace(/\n/g, "<br/>"),
        body_text: reply,
      });
      toast.success("تم إرسال الرد");
      setReply("");
      const { data } = await api.get(`/admin/inbox/${selected.id}`);
      setMsgs(data.messages || []);
      loadThreads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الإرسال");
    } finally {
      setSending(false);
    }
  };

  const archiveThread = async () => {
    if (!selected) return;
    await api.post(`/admin/inbox/${selected.id}/${archived ? "unarchive" : "archive"}`);
    toast.success(archived ? "أُلغيت الأرشفة" : "تمت الأرشفة");
    setSelected(null);
    loadThreads();
  };

  const deleteThread = async () => {
    if (!selected || !window.confirm("حذف نهائيًا؟")) return;
    await api.delete(`/admin/inbox/${selected.id}`);
    toast.success("تم الحذف");
    setSelected(null);
    loadThreads();
  };

  useEffect(() => { loadThreads(); }, [archived]);
  // Polling every 30s so new replies appear without a manual refresh
  useEffect(() => {
    const t = setInterval(loadThreads, 30000);
    return () => clearInterval(t);
  }, [archived, search]);

  return (
    <div className="space-y-4" dir="rtl" data-testid="admin-inbox">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-semibold flex items-center gap-2">
            <Inbox className="h-6 w-6" />
            صندوق الوارد
            {totalUnread > 0 && (
              <span className="bg-red-600 text-white text-xs rounded-full px-2 py-0.5">{totalUnread}</span>
            )}
          </h1>
          <p className="text-foreground/60 mt-1 text-sm">
            الردود على رسائلك الإلكترونية تصل هنا. رد مباشرةً من هذه الصفحة وسيصل للعميل في نفس المحادثة.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex gap-1">
            <button
              onClick={() => setArchived(false)}
              className={`px-3 py-1.5 rounded text-xs border ${!archived ? "bg-black text-white border-foreground" : "bg-white border-foreground/10"}`}
              data-testid="inbox-tab-active"
            >نشط</button>
            <button
              onClick={() => setArchived(true)}
              className={`px-3 py-1.5 rounded text-xs border ${archived ? "bg-black text-white border-foreground" : "bg-white border-foreground/10"}`}
              data-testid="inbox-tab-archived"
            >المؤرشف</button>
          </div>
          <Button size="sm" variant="ghost" onClick={loadThreads} data-testid="inbox-refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              const { data } = await api.post(`/admin/inbox/backfill-bodies?limit=200`);
              toast.success(`تم تحديث ${data.updated} رسالة (تم تخطي ${data.skipped}، فشل ${data.failed})`);
              if (selected) openThread(selected);
              loadThreads();
            }}
            title="إعادة جلب نصوص الرسائل القديمة من Resend"
            data-testid="inbox-backfill"
          >
            إعادة جلب النصوص
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3 xl:grid-cols-4">
        {/* Threads list */}
        <div className="lg:col-span-1 xl:col-span-1 space-y-2">
          <form onSubmit={(e) => { e.preventDefault(); loadThreads(); }} className="flex gap-1">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="بحث..."
              className="bg-white border-foreground/10 text-foreground text-sm"
              data-testid="inbox-search"
            />
            <Button type="submit" size="sm" variant="outline"><Search className="h-4 w-4" /></Button>
          </form>
          <Card className="p-0 overflow-hidden border-foreground/10">
            {threads.length === 0 ? (
              <div className="p-6 text-xs text-foreground/50 text-center">
                لا رسائل بعد. عند رد أحد العملاء على رسائلك ستظهر هنا.
              </div>
            ) : (
              <ul className="divide-y divide-black/5 max-h-[70vh] overflow-y-auto">
                {threads.map((t) => (
                  <li
                    key={t.id}
                    onClick={() => openThread(t)}
                    className={`p-3 cursor-pointer hover:bg-foreground/5 ${selected?.id === t.id ? "bg-primary/5" : ""}`}
                    data-testid={`inbox-thread-${t.id}`}
                  >
                    <div className="flex items-center gap-2">
                      {t.unread_count > 0 ? (
                        <Mail className="h-3.5 w-3.5 text-primary shrink-0" />
                      ) : (
                        <MailOpen className="h-3.5 w-3.5 text-foreground/40 shrink-0" />
                      )}
                      <span className={`text-sm truncate flex-1 ${t.unread_count > 0 ? "font-semibold" : ""}`}>
                        {t.last_from_name || t.last_from}
                      </span>
                      <span className="text-[10px] text-foreground/40 shrink-0">{fmtDate(t.last_message_at)}</span>
                    </div>
                    <div className={`text-xs mt-0.5 truncate ${t.unread_count > 0 ? "text-foreground font-medium" : "text-foreground/60"}`}>
                      {t.subject || "(بدون موضوع)"}
                    </div>
                    <div className="text-[10px] text-foreground/50 mt-1 truncate">{t.last_snippet}</div>
                    {t.unread_count > 1 && (
                      <span className="text-[10px] bg-red-100 text-red-700 rounded px-1.5 py-0.5 mt-1 inline-block">
                        {t.unread_count} غير مقروءة
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        {/* Thread view */}
        <div className="lg:col-span-2 xl:col-span-3">
          {!selected ? (
            <Card className="p-10 text-center text-foreground/50 text-sm">اختر محادثة من القائمة لعرضها</Card>
          ) : (
            <Card className="p-4 border-foreground/10">
              <div className="flex items-start justify-between border-b border-foreground/5 pb-3 mb-3 gap-2 flex-wrap">
                <div className="flex-1 min-w-0">
                  <h2 className="font-semibold text-lg truncate">{selected.subject || "(بدون موضوع)"}</h2>
                  <p className="text-xs text-foreground/60 mt-0.5">
                    مع {selected.last_from_name ? `${selected.last_from_name} · ` : ""}
                    <span className="font-mono">{selected.last_from}</span> · {selected.message_count} رسالة
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={archiveThread} data-testid="inbox-archive">
                    <Archive className="h-3.5 w-3.5 me-1" />{archived ? "استعادة" : "أرشفة"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={deleteThread} className="text-red-700" data-testid="inbox-delete">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              <div className="space-y-3 max-h-[50vh] overflow-y-auto pe-2">
                {msgs.map((m) => (
                  <div
                    key={m.id}
                    className={`rounded-lg p-3 border ${m.direction === "out" ? "bg-primary/5 border-primary/20 me-4" : "bg-white border-foreground/10 ms-4"}`}
                    data-testid={`inbox-msg-${m.id}`}
                  >
                    <div className="flex items-center justify-between text-[11px] text-foreground/60 mb-2">
                      <span className="font-mono">
                        {m.direction === "out" ? "أنت" : (m.from_name || m.from_email)}
                      </span>
                      <span>{fmtDate(m.created_at)}</span>
                    </div>
                    {m.html ? (
                      <div className="prose prose-sm max-w-none text-sm" dangerouslySetInnerHTML={{ __html: m.html }} />
                    ) : (
                      <div className="text-sm whitespace-pre-wrap">{m.text}</div>
                    )}
                    {m.attachments?.length > 0 && (
                      <div className="mt-2 text-[10px] text-foreground/60">
                        📎 {m.attachments.length} مرفق
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {!archived && (
                <div className="mt-4 border-t border-foreground/5 pt-3">
                  <label className="text-xs text-foreground/60 block mb-1">الرد</label>
                  <Textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="اكتب ردك هنا..."
                    rows={5}
                    className="bg-white border-foreground/10 text-foreground"
                    data-testid="inbox-reply-textarea"
                  />
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-[10px] text-foreground/50">
                      سيصل الرد من <span className="font-mono">support@faisalfit.com</span> في نفس المحادثة.
                    </span>
                    <Button
                      onClick={sendReply}
                      disabled={sending || !reply.trim()}
                      data-testid="inbox-send-reply"
                    >
                      <Send className="h-4 w-4 me-1" />
                      {sending ? "جاري الإرسال..." : "إرسال الرد"}
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffH = (now - d) / 3600000;
  if (diffH < 24) return d.toLocaleTimeString("ar-SA", { hour: "2-digit", minute: "2-digit" });
  if (diffH < 24 * 7) return d.toLocaleDateString("ar-SA", { weekday: "short" });
  return d.toLocaleDateString("ar-SA", { month: "short", day: "numeric" });
}
